// @givmo/org-console — the EXTERNAL mark (§OM-53): an admin the organization has marked as from
// outside it, such as an outside accountant made a Books-only admin. The ONE place its words and its
// looks live: the note a marked person's name carries, the "About this person" section (its switch
// card) the access editor and the invite composer carry, and the sentences Activity and the invite
// composer's already-an-admin outcome say about it.
//
// ⚠ A LABEL, NEVER ACCESS. The mark changes nothing a person can do, so nothing in this package may
// gate, route, count or mail on it. It is read only to SHOW it: beside a name on every roster that
// lists the person, on a pending invite, on an access request, and in the editor's title. The card's
// own line says so to the person about to turn it on, because that is the first thing they will want
// to know.
//
// ⚠ A NOTE ABOUT THE PERSON, NOT A LEVEL. It is muted text on the name's own line, never a badge:
// the roster's Access column speaks in badges ("Global Admin", "Member"), and a third badge on the
// same row read as one more kind of access ("External … Member"), which is exactly what it isn't.
//
// ⚠ ONLY ON THE EXCEPTION. An unmarked name carries nothing (no "Staff"), so the note is the one
// thing that differs about a row, and a roster of staff looks exactly as it did.
//
// THE WORDS' STANDING. Jonathan approved the External label's screens as drawn, with every word on
// them (D-CPO-67, 2026-10-23 21:44 PDT: "the demotion screen and External label screens look good";
// the frames, their README and its words table are in the plan's
// `scratchpad/2026-10-23-0642-om53-web-mockup-frames/`). They are byte-identical here, and
// `copy-law.test.ts` pins each one: changing a word changes a ruling. One sentence ruled apart for a
// Global Admin on an app's own invite (D-CPO-73) is gone: since OM-96 (D-CPO-77) a Global Admin's
// Team page inside an app is Team access in place, so no Global Admin meets that door any more.
//
// THE CLASS ENTERS AS DATA (R5): the host's noun and its app names come from its vocab.

import { Switch, testIdProp } from '@givmo/design-system';

export const EXTERNAL_COPY = {
  /** The mark's word, the same on both consoles: the note after a name, and the switch's title. */
  label: 'External',
  /** The small label over the mark's switch: it is about who the person is, not what they can open. */
  aboutThisPerson: 'About this person',
  /** What the mark means, in the host's own noun: the line under the switch that sets it. */
  line: (orgNoun: string): string =>
    `Someone from outside this ${orgNoun}, such as an outside accountant or lawyer. It’s a label only and doesn’t change what they can do.`,
  /**
   * After the line on an app's own invite form, for an issuer who cannot manage the person's access
   * afterwards: the invite is their one chance to set the label (§OM-53 §3.1).
   */
  onlyAGlobalAdminLater: 'Only a Global Admin can change this later.',
  /** Activity's headlines, read off the row's `to.external`. */
  marked: 'Marked as external',
  unmarked: 'No longer marked as external',
  /** Activity's NEUTRAL headline, for a row whose payload cannot say which way the mark moved. */
  changed: 'External label changed',
  /** Activity's detail for the row an invite's accept writes (`via: "invite"`). */
  fromTheirInvitation: 'From their invitation',
  /**
   * Already an admin, External on, from Team access: the remedy leads with the act (M15a). It
   * replaces today's remedy only when External was on.
   */
  alreadyAdminTeamList:
    'To mark them as external, find them in the team list and open their access.',
  /** The same from an app's own Team page, by an issuer who cannot set it there (M15b). */
  alreadyAdminOnlyAGlobalAdmin: 'Only a Global Admin can mark them as external.',
} as const;

/**
 * The note a marked person's name carries: " · External", muted, on the name's baseline. The dot is
 * decoration only, so a screen reader hears the word alone.
 */
export function ExternalNote({ testId }: { testId: string }) {
  return (
    <span
      className="inline-flex shrink-0 items-baseline font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
      {...testIdProp(testId)}
    >
      <span aria-hidden className="mr-6 text-text-tertiary">
        ·
      </span>
      {EXTERNAL_COPY.label}
    </span>
  );
}

export interface ExternalCardProps {
  orgNoun: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  testId: string;
  /** One more sentence after the line, where the person setting it should know something more. */
  note?: string;
}

/** The switch that sets the mark: the Global Admin card's shape, so the two read as one family. */
export function ExternalCard({
  orgNoun,
  checked,
  onCheckedChange,
  testId,
  note,
}: ExternalCardProps) {
  return (
    <div
      className="flex items-start justify-between gap-16 rounded-lg border border-border-subtle p-16"
      {...testIdProp(testId)}
    >
      <div className="min-w-0">
        <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {EXTERNAL_COPY.label}
        </p>
        <p className="mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          {EXTERNAL_COPY.line(orgNoun)}
          {note !== undefined && <> {note}</>}
        </p>
      </div>
      <Switch
        appearance="saas"
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={EXTERNAL_COPY.label}
        testId={`${testId}-switch`}
      />
    </div>
  );
}

/**
 * The mark's section: its small label, then its card. The label keeps the card from reading as one
 * more app among the access controls around it.
 */
export function ExternalSection(props: ExternalCardProps) {
  return (
    <div className="flex flex-col gap-8" {...testIdProp(`${props.testId}-section`)}>
      <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
        {EXTERNAL_COPY.aboutThisPerson}
      </p>
      <ExternalCard {...props} />
    </div>
  );
}
